import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Menu } from 'components/Menu/Menu'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Item, Section } from '@react-stately/collections'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Avatar from 'common/Avatar/Avatar'
import { getInitials } from 'utils'
import { Text } from 'blaze-ui/Text/Text'
import UserProfileAdd2 from 'blaze-ui/icons/20/UserProfileAdd2'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { useInitiateOauthTrigger } from 'hooks/useInitiateOauthTrigger/useInitiateOauthTrigger'
import Styles from './IntegrationSelector.module.scss'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useMemo, useRef } from 'react'
import { useAccountLimitStatus } from 'hooks/useAccountLimitStatus'

interface IntegrationSelectorProps {
  integrations: ExternalIntegration[]
  selectedIntegrationIds?: number[]
  onSelect: (integrationId: number) => void
  onAddNewAccount: (popUp?: Window) => void
  platform: ExternalIntegrationEnum
  trigger: React.ReactElement
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  disableLastItem?: boolean
}

export const IntegrationSelector: React.FC<IntegrationSelectorProps> = ({
  integrations,
  selectedIntegrationIds = [],
  onSelect,
  onAddNewAccount,
  platform,
  trigger,
  isOpen,
  onOpenChange,
  placement = 'bottom-start',
  disableLastItem = false,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { handleConnect } = useInitiateOauthTrigger({
    provider: platform,
    popUp: true,
    onInitiatedOauth: onAddNewAccount,
  })
  const { isAtOrAboveMaxAccounts } = useAccountLimitStatus()

  const disabledIntegrationId = useMemo(() => {
    if (disableLastItem && selectedIntegrationIds.length === 1) {
      return selectedIntegrationIds[0]
    }
    return undefined
  }, [disableLastItem, selectedIntegrationIds])

  const disabledKeys = useMemo(() => {
    if (disabledIntegrationId) {
      return new Set([disabledIntegrationId.toString()])
    }
    return new Set<string>()
  }, [disabledIntegrationId])

  const handleAction = (key: string | number) => {
    if (key === 'add-new-account') {
      onOpenChange?.(false)
      handleConnect()
    } else {
      // Prevent unchecking if it's the disabled integration
      if (
        disabledIntegrationId &&
        Number(key) === disabledIntegrationId &&
        selectedIntegrationIds.includes(Number(key))
      ) {
        return
      }
      onSelect(Number(key))
    }
  }

  const menu = (
    <Menu
      aria-label="Select integration"
      selectionMode="multiple"
      selectedKeys={selectedIntegrationIds.map((id) => id.toString())}
      onAction={handleAction}
      className={Styles.menu}
      disabledKeys={disabledKeys}
      showCheckmark={false}
      showCheckbox
    >
      {integrations.map((integration) => {
        const { avatar, name, email, username } = extractProfileInfo(integration)
        const subLabel = email || username
        const avatarUrl = avatar || Silhouette
        const isDisabled = disabledIntegrationId === integration.id
        const showTooltip = disableLastItem && isDisabled

        const integrationContent = (
          <div className={Styles.integrationItem}>
            <Avatar size={32} url={avatarUrl} label={getInitials(name)} altText={name} />
            <div className={Styles.integrationDetails}>
              <Text variant="smallList" color="var(--dark-80)">
                {name}
              </Text>
              {subLabel && (
                <Text variant="metadata" color="var(--dark-40)">
                  {subLabel}
                </Text>
              )}
            </div>
          </div>
        )

        return (
          <Item key={integration.id} textValue={name} data-close-on-select={false}>
            {showTooltip ? (
              <Tooltip label="You cannot disable the last platform while the post is scheduled">
                {integrationContent}
              </Tooltip>
            ) : (
              integrationContent
            )}
          </Item>
        )
      })}
      <Section>
        <Item key="add-new-account" textValue="Add New Account" data-hide-checkbox>
          <div className={Styles.addNewAccountItem}>
            <UserProfileAdd2 size={20} color="var(--dark-80)" />
            <div>
              <Text variant="smallList" color="var(--dark-80)">
                Connect Extra Account
              </Text>
              {isAtOrAboveMaxAccounts && (
                <Text variant="metadata" color="var(--dark-40)">
                  $10 per month
                </Text>
              )}
            </div>
          </div>
        </Item>
      </Section>
    </Menu>
  )

  return (
    <div ref={ref}>
      <MenuTrigger isOpen={isOpen} onOpenChange={onOpenChange} popoverTargetRef={ref} placement={placement}>
        {trigger}
        {menu}
      </MenuTrigger>
    </div>
  )
}
